/* ============================================================================
 * gtckt-plans-flow.js — central state machine for the multi-step onboarding
 * flow at /pages/plans (and /pages/pick-meals).
 *
 * Responsibilities:
 *   - sessionStorage-backed state object (window.gtcktState)
 *   - Page-level step navigation: window.gtcktAdvanceStep(n)
 *   - Storefront API customerCreate / customerAccessTokenCreate helpers
 *   - Cart-add + checkout redirect with address pre-fill
 *   - Step-bar DOM sync (toggles .gc-gtckt-plans-step-bar-active / -done)
 *
 * Section JS files (builder, register, address, pick-meals) call into this
 * module rather than driving step transitions themselves.
 * ============================================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'gtckt_plans_state';
  var STEP_BAR_PREFIX = 'gc-gtckt-plans-step-bar-';

  // ── Section wrapper ids the flow shows/hides as it advances. Each section's
  // root div on the plans page should have id="gtckt-step-<N>".
  // Step 1 = builder, 2 = register, 3 = pick-meals. Step 4 = checkout (off-page).
  var STEP_SECTION_IDS = {
    1: 'gtckt-step-1',
    2: 'gtckt-step-2',
    3: 'gtckt-step-3',
  };

  // ── Optional Register step (step 2) ─────────────────────────────────────────
  // Merchants can remove or disable the Register section in the theme editor.
  // When absent, the step bar collapses to 1 Select Plan → 2 Pick Meals →
  // 3 Payment, and any code that calls gtcktAdvanceStep(2) is redirected.
  function hasRegisterStep() {
    return !!document.getElementById(STEP_SECTION_IDS[2]);
  }
  function visibleSteps() {
    return hasRegisterStep() ? [1, 2, 3, 4] : [1, 3, 4];
  }

  // ── Shared pricing table (must mirror gc-gtckt-plans-builder.liquid) ────────
  // Keep these in sync. Used by the order-summary in the address section.
  window.gtcktPricing = {
    6:  { box: 83.94,  orig: 13.99, disc: 8.39, ship: 10.99, savings: 44.59 },
    8:  { box: 111.92, orig: 13.99, disc: 8.39, ship: 10.99, savings: 55.76 },
    10: { box: 129.90, orig: 12.99, disc: 7.79, ship: 10.99, savings: 62.99 },
    12: { box: 143.88, orig: 11.99, disc: 7.19, ship: 10.99, savings: 68.59 },
    14: { box: 153.86, orig: 10.99, disc: 6.59, ship: 10.99, savings: 72.59 },
    18: { box: 179.82, orig:  9.99, disc: 5.99, ship: 10.99, savings: 82.99 }
  };

  // ── State (sessionStorage-backed) ───────────────────────────────────────────
  function readState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeState(s) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch (e) { /* quota exceeded or disabled — ignore */ }
  }

  var state = readState();

  window.gtcktState = {
    get: function (key) { return state[key]; },
    set: function (key, value) { state[key] = value; writeState(state); },
    all: function () { return Object.assign({}, state); },
    clear: function () {
      state = {};
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
    },
  };

  // ── Step-bar DOM sync ───────────────────────────────────────────────────────
  function setActiveStepInBar(n) {
    var bar = document.getElementById('stepBar');
    if (!bar) return;
    bar.dataset.activeStep = String(n);
    var visible = visibleSteps();
    var registerPresent = hasRegisterStep();
    for (var i = 1; i <= 4; i++) {
      var item = document.getElementById('s' + i);
      var line = document.getElementById('sl' + i);
      // Step 2 (and its trailing connector sl2) collapse when Register is gone.
      if (i === 2 && !registerPresent) {
        if (item) item.style.setProperty('display', 'none', 'important');
        if (line) line.style.setProperty('display', 'none', 'important');
        continue;
      }
      // Reset display in case Register was toggled back on after a hide.
      if (item && item.style.getPropertyValue('display') === 'none') item.style.removeProperty('display');
      if (line && line.style.getPropertyValue('display') === 'none') line.style.removeProperty('display');

      if (item) {
        item.classList.remove(STEP_BAR_PREFIX + 'active', STEP_BAR_PREFIX + 'done');
        var num = item.querySelector('.' + STEP_BAR_PREFIX + 'step-num');
        var displayNum = visible.indexOf(i) + 1; // 1-based position among visible steps
        if (i < n) {
          item.classList.add(STEP_BAR_PREFIX + 'done');
          if (num) num.textContent = '✓';
        } else if (i === n) {
          item.classList.add(STEP_BAR_PREFIX + 'active');
          if (num) num.textContent = String(displayNum);
        } else {
          if (num) num.textContent = String(displayNum);
        }
      }
      if (line) {
        line.classList.toggle(STEP_BAR_PREFIX + 'done', i < n);
      }
    }
  }

  // ── Section show/hide ───────────────────────────────────────────────────────
  function showOnlyStep(n) {
    Object.keys(STEP_SECTION_IDS).forEach(function (k) {
      var el = document.getElementById(STEP_SECTION_IDS[k]);
      if (!el) return;
      el.style.display = (Number(k) === n) ? '' : 'none';
    });
    var footer = document.getElementById('priceFooter');
    if (footer) {
      // Footer shows on steps that have pricing context (Builder + Pick Meals).
      // Hidden on Register (step 2) — no pricing relevance there.
      footer.style.display = (n === 1 || n === 3) ? '' : 'none';
    }
  }

  // ── Public step nav ─────────────────────────────────────────────────────────
  window.gtcktAdvanceStep = function (n) {
    if (n < 1 || n > 4) return;
    // Skip step 2 if Register section is absent; redirect based on direction.
    if (n === 2 && !hasRegisterStep()) {
      var current = state.currentStep || 1;
      n = (current > 2) ? 1 : 3;
    }
    state.currentStep = n;
    writeState(state);
    setActiveStepInBar(n);
    showOnlyStep(n);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.dispatchEvent(new CustomEvent('gtckt:stepChanged', { detail: { step: n } }));
  };

  // ── Cart-add + checkout redirect ───────────────────────────────────────────
  // Called from Step 3 (pick-meals) with an array of picks:
  //   [{ variantId, quantity, sellingPlanId }, ...]
  // Each entry becomes a /cart/add.js line item. Email pre-fills the checkout URL.
  // Shipping address is collected by Shopify's checkout itself (we no longer
  // have an in-flow address step).
  window.gtcktAddToCartAndCheckout = function (picks) {
    var s = state;
    if (!Array.isArray(picks) || !picks.length) {
      return Promise.reject(new Error('No meals selected.'));
    }
    if (!s.email) {
      return Promise.reject(new Error('Missing email — return to the Register step.'));
    }

    var items = picks.map(function (p) {
      var item = {
        id: p.variantId,
        quantity: p.quantity || 1,
        properties: {
          _collections: (s.collections || []).join(','),
          _meal_count: String(s.mealCount || ''),
          // Markers that trigger the Shopify-side "Build a Box Discount"
          // automatic discount. Must mirror ALL four properties from the
          // existing build-a-box section ([section-bundle.liquid:1384-1389])
          // — the discount keys off _bundleType + _dynamicBoxId. The id below
          // is the live build-a-box page's box id (page.build-a-box.json),
          // shared so both flows roll up under the same discount config.
          _dynamicBoxIndex: 0,
          _dynamicBoxId: '09a104f3-6cde-47cc-89b6-e87d3e1c734e',
          _bundleType: 'dynamic',
          'Build a Box': 'Item',
        },
      };
      if (p.sellingPlanId) item.selling_plan = p.sellingPlanId;
      return item;
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return { description: 'cart-add failed' }; }).then(function (err) {
          throw new Error(err.description || ('cart-add failed: ' + r.status));
        });
      }
      return r.json();
    }).then(function () {
      // Stash the email on cart attributes too — some checkout configs honor
      // these for pre-fill while ignoring URL query params.
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { 'Customer Email': s.email || '' },
        }),
      }).catch(function () { /* non-fatal — keep going */ });
    }).then(function () {
      // Pre-fill email at checkout. Both formats — older Shopify checkout uses
      // 'checkout[email]', newer extensibility checkout sometimes prefers 'email'.
      var params = new URLSearchParams({
        'checkout[email]': s.email,
        'email': s.email,
      });
      var url = '/checkout?' + params.toString();
      console.info('[gtckt] redirecting to checkout:', url, 'email:', s.email);
      window.location.href = url;
    });
  };

  // ── Klaviyo subscribe (fire-and-forget) ────────────────────────────────────
  // Posts to Klaviyo's Client Subscriptions endpoint — CORS-enabled, public-key
  // only, no server proxy needed. Subscribes the profile to the configured list
  // with marketing consent so abandoned-flow emails can target them even if
  // they never reach checkout. Idempotent — Klaviyo de-dupes by email.
  // Docs: https://developers.klaviyo.com/en/reference/create_client_subscription
  window.gtcktSubscribeToKlaviyo = function (email, source) {
    var cfg = (window.gtcktConfig && window.gtcktConfig.klaviyo) || {};
    if (!cfg.companyId || !cfg.listId) {
      console.info('[gtckt] Klaviyo not configured — skipping subscribe.');
      return Promise.resolve({ skipped: true });
    }
    if (!email) return Promise.resolve({ skipped: true });

    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.companyId);
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'Plans Register',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: cfg.listId } }
        }
      }
    };

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify(body),
      // keepalive: lets the request survive a same-tab navigation, so the
      // Register → Pick-Meals advance doesn't cancel the subscribe in flight.
      keepalive: true
    }).then(function (r) {
      if (!r.ok) {
        return r.text().then(function (t) {
          console.warn('[gtckt] Klaviyo subscribe non-OK:', r.status, t);
        });
      }
      console.info('[gtckt] Klaviyo subscribed:', email);
    }).catch(function (e) {
      console.warn('[gtckt] Klaviyo subscribe failed (non-fatal):', e);
    });
  };

  // ── On load: restore the user's last step if mid-flow ──────────────────────
  // Only relevant on the main plans page (which has gtckt-step-1). Step 4
  // (checkout) lives off-domain, so we clamp resume to 3 if state somehow
  // advanced past pick-meals.
  document.addEventListener('DOMContentLoaded', function () {
    if (!document.getElementById(STEP_SECTION_IDS[1])) return;
    // Always sync the bar on load so the Register step is hidden from the
    // start when its section isn't present (not just after the first advance).
    setActiveStepInBar(state.currentStep || 1);
    if (!state.currentStep || state.currentStep <= 1) return;
    var resumeStep = state.currentStep > 3 ? 3 : state.currentStep;
    if (resumeStep === 2 && !hasRegisterStep()) resumeStep = 3;
    window.gtcktAdvanceStep(resumeStep);
  });
})();
